/* eslint-disable react/prop-types */
import React, { useMemo } from "react";
import statusColor from "../utils/satus";
import { Collapse, Spin, Tag, Table, Typography } from "antd";

const { Title } = Typography;
const { Panel } = Collapse;

/** Hungama catalog id — platforms that syndicate through Hungama use linked_platform_id. */
const HUNGAMA_PLATFORM_ID = "WdkSRjlvGdM24eJpym8D";

const tag = (item) => (
  <Tag
    color={
      (statusColor[item.status] && statusColor[item.status].color) || "warning"
    }
    icon={statusColor[item.status] && statusColor[item.status].icon}
  >
    {item.status}
  </Tag>
);

const columns = [
  {
    title: "Terms",
    dataIndex: "description",
    key: "description",
    width: "40%",
  },
  {
    title: "Status",
    key: "status_message",
    render: (_, record) => {
      const note =
        (record.msg && String(record.msg).trim()) ||
        (record.link && String(record.link).trim()) ||
        "";
      const watchUrl = (record.link && String(record.link).trim()) || "";
      return (
        <>
          {tag(record)}
          <br /> <br />
          {["blocked", "rejected", "offline"].includes(record.status) ? (
            `Message from Platform - "${note}"`
          ) : record.status == "live" ? (
            <>
              Your film is live. Watch it{" "}
              {watchUrl ? (
                <u>
                  <a href={watchUrl} target="_blank" rel="noreferrer">
                    here
                  </a>
                </u>
              ) : (
                "—"
              )}
              .<br />
            </>
          ) : (
            "The film is under QC process. You will be notified once it starts streaming."
          )}
        </>
      );
    },
  },
];

function hasCatalogTerms(item) {
  return (
    item.exclusive != null ||
    item.rev_share != null ||
    item.value_text ||
    item.mode ||
    item.period_years != null ||
    item.payout ||
    item.metric_label
  );
}

function buildTermsDescription(item) {
  if (item.status !== "live") return null;

  const streaming = Boolean(item.link);
  const catalog = hasCatalogTerms(item);
  const hungamaSyndicated = item.linked_platform_id === HUNGAMA_PLATFORM_ID;

  // Syndicated-through-Hungama note when there are no catalog columns (legacy path).
  if (hungamaSyndicated && !catalog) {
    return (
      <ul style={{ paddingLeft: 5 }}>
        {streaming && <li>Streaming link from distribution.</li>}
        <li>
          This platform is linked to Hungama Play and revenue reports will be
          integrated with Hungama Play.
        </li>
      </ul>
    );
  }

  if (!streaming && !catalog) return null;

  return (
    <ul style={{ paddingLeft: 5 }}>
      {streaming && <li>Streaming link from distribution.</li>}
      {catalog && (
        <>
          <li>{item.exclusive ? "Exclusive" : "Non-Exclusive"}</li>
          <li>
            {item.rev_share ? "Revenue Share" : "One-Time"}
            {item.value_text != null && item.value_text !== ""
              ? ` | ${item.value_text}${item.rev_share ? "%" : ""}`
              : ""}
          </li>
          {item.mode && <li>{item.mode}</li>}
          {item.period_years != null && <li>{item.period_years} years</li>}
          {item.payout && (
            <li>
              {item.payout.charAt(0).toUpperCase() + item.payout.slice(1)}{" "}
              payouts
            </li>
          )}
          {item.metric_label && <li>{item.metric_label}</li>}
        </>
      )}
    </ul>
  );
}

const Platform = ({ distribution, loading }) => {
  const panels = useMemo(() => {
    if (!distribution?.platform?.length) return [];
    return distribution.platform.map((item) => ({
      ...item,
      panelHeaderText: item.display_name || item.id,
      description: buildTermsDescription(item),
    }));
  }, [distribution?.platform]);

  if (loading) {
    return (
      <>
        <Title level={4}>Platforms</Title>
        <Collapse activeKey={["_loading"]}>
          <Panel header="Loading platforms…" key="_loading">
            <div style={{ padding: "24px 0", textAlign: "center" }}>
              <Spin size="large" />
            </div>
          </Panel>
        </Collapse>
      </>
    );
  }

  return (
    <>
      {panels.length > 0 && (
        <>
          <Title level={4}>Platforms</Title>
          <Collapse defaultActiveKey={["0"]}>
            {panels.map((item) => (
              <Panel
                key={item.id}
                header={item.panelHeaderText}
                extra={tag(item)}
              >
                <Table
                  dataSource={[item]}
                  columns={columns}
                  pagination={false}
                />
              </Panel>
            ))}
          </Collapse>
        </>
      )}
    </>
  );
};

export default Platform;
